import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Column } from '../../components/DataTable/DataTable';
import { GenericListPage } from '../../components/generic';
import { useDeletedFileAttachments, DeletedFileAttachment } from './useData';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../config/appConfig';
import type { FieldConfig } from '../../components/generic/fieldRenderers';
import { permanentDeleteFileAttachment, deleteOrphanedFileAttachments, deleteFilesWithoutRecord } from '../../services/mutations';
import api from '../../services/api';

type AuditItem = {
  id: number;
  fileName: string | null;
  filePath: string | null;
  reason: 'orphaned' | 'no_path';
};

type AuditResult = {
  totalRecords: number;
  withFileOnDisk: number;
  orphanedRecords: number;
  noFilePath: number;
  orphanedIds: number[];
  list: AuditItem[];
  filesWithoutRecord: string[];
};

const MODULE_ROUTE = '/admin/settings/files-storage';

const moduleConfig = {
  module: {
    title: 'File storage',
    description: 'Audit file attachments and manage deleted files',
    route: {
      list: MODULE_ROUTE,
      new: MODULE_ROUTE,
      detail: (id: number) => `${MODULE_ROUTE}/${id}`,
      edit: (id: number) => `${MODULE_ROUTE}/${id}/edit`,
    },
    api: { list: '/file-attachment/deleted', detail: (id: number) => `/file-attachment/${id}` },
  },
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },
  search: { placeholder: 'Search by file name or description...' },
  filterGroups: [],
  filters: {
    fields: [],
    applyFilter: (items: DeletedFileAttachment[]) => items,
  },
  toolbar: { actions: [] },
  table: {
    actionsPosition: 'right' as const,
    keyExtractor: (item: DeletedFileAttachment) => item.id,
  },
  emptyStates: {
    noData: 'No deleted files.',
    noSearchResults: 'No matches.',
    noFilterResults: 'No matches.',
  },
  delete: {
    title: 'Delete permanently',
    message: 'Permanently delete this record and the file from disk? This cannot be undone.',
    confirmText: 'Delete permanently',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete.',
    handler: async (_id: number) => {},
  },
  utils: { formatDate: formatDateGlobal },
  fields: [] as FieldConfig[],
};

type EntityLink = { entityType: string; href: string; primary: string; secondary?: string };

function getEntityLink(row: DeletedFileAttachment): EntityLink | null {
  if (row.clientId && row.client) {
    return { entityType: 'Client', href: `/clients/${row.client.clientId}`, primary: row.client.clientName || `#${row.client.clientId}` };
  }
  if (row.taskId && row.task) {
    const desc = (row.task.taskDescription || '').slice(0, 50);
    return { entityType: 'Task', href: `/crm_tasks/${row.task.taskId}`, primary: desc || `Task #${row.task.taskId}`, secondary: row.task.taskDate ? formatDateGlobal(row.task.taskDate) : undefined };
  }
  if (row.appointmentId && row.appointment) {
    const name = row.appointment.clientAppName || `Appointment #${row.appointment.clientAppId}`;
    const client = row.appointment.client?.clientName;
    return { entityType: 'Appointment', href: `/appointments/${row.appointment.clientAppId}`, primary: name, secondary: client ?? undefined };
  }
  if (row.invoiceId && row.invoice) {
    const no = row.invoice.InvoiceNo || `#${row.invoice.invoiceId}`;
    const client = row.invoice.client?.clientName;
    return { entityType: 'Invoice', href: `/client-invoices/${row.invoice.invoiceId}`, primary: no, secondary: client ?? undefined };
  }
  if (row.leadId && row.lead) {
    const desc = (row.lead.leadDescr || '').slice(0, 50);
    return { entityType: 'Lead', href: `/leads/${row.lead.leadId}`, primary: desc || `Lead #${row.lead.leadId}` };
  }
  if (row.todoId && row.todo) {
    const desc = (row.todo.todoDescr || '').slice(0, 40) || `Todo #${row.todo.todoId}`;
    return { entityType: 'Todo', href: `/todos/${row.todo.todoId}`, primary: desc };
  }
  if (row.supportTicketId && row.supportTicket) {
    return { entityType: 'Support Ticket', href: `/support-ticket/${row.supportTicket.id}`, primary: row.supportTicket.reference, secondary: row.supportTicket.title };
  }
  return null;
}

function getColumns(formatDate: (dateString?: string) => string): Column<DeletedFileAttachment>[] {
  return [
    { key: 'fileName', header: 'File name', sortable: true, render: (v) => v || '—' },
    { key: 'fileType', header: 'Type', sortable: true, render: (v) => v || '—' },
    { key: 'fileSize', header: 'Size', sortable: true, render: (v) => (v != null ? `${(v / 1024).toFixed(1)} KB` : '—') },
    { key: 'entityType', header: 'Entity', sortable: false, render: (_, row) => getEntityLink(row)?.entityType ?? '—' },
    { key: 'linkedRecord', header: 'Linked', sortable: false, render: (_, row) => {
      const link = getEntityLink(row);
      if (!link) return '—';
      return <Link to={link.href} className="text-indigo-600 hover:underline" onClick={(e) => e.stopPropagation()}>{link.primary}</Link>;
    }},
    { key: 'uploadedAt', header: 'Uploaded', sortable: true, render: (v) => formatDate(v) },
  ];
}

function getTableActions(item: DeletedFileAttachment, onDelete: (id: number) => void): React.ReactNode {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onDelete(item.id); }}
      className="inline-flex items-center gap-1.5 px-2 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 rounded"
      title="Delete permanently"
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
      Delete
    </button>
  );
}

const StorageIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H5zM19 19a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2zM5 11h14" />
  </svg>
);

const FilesStoragePage: React.FC = () => {
  const { colors } = useTheme();
  const { mutate } = useDeletedFileAttachments();
  const useData = () => useDeletedFileAttachments();
  const [audit, setAudit] = useState<AuditResult | null>(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState<string | null>(null);
  const [deletingOrphaned, setDeletingOrphaned] = useState(false);
  const [deletingFilesWithoutRecord, setDeletingFilesWithoutRecord] = useState(false);

  const runAudit = async () => {
    setAuditLoading(true);
    setAuditError(null);
    try {
      const res = await api.get<AuditResult>('/file-attachment/audit');
      setAudit(res.data);
    } catch (e: unknown) {
      setAuditError(e instanceof Error ? e.message : 'Failed to run audit');
      setAudit(null);
    } finally {
      setAuditLoading(false);
    }
  };

  const handleDeleteOrphaned = async () => {
    const count = (audit?.orphanedRecords ?? 0) + (audit?.noFilePath ?? 0);
    if (count === 0) return;
    if (!window.confirm(`Delete ${count} orphaned record(s)? This cannot be undone.`)) return;
    setDeletingOrphaned(true);
    setAuditError(null);
    try {
      await deleteOrphanedFileAttachments();
      await runAudit();
    } catch (e: unknown) {
      setAuditError(e instanceof Error ? e.message : 'Failed to delete orphaned records');
    } finally {
      setDeletingOrphaned(false);
    }
  };

  const handleDeleteFilesWithoutRecord = async () => {
    const count = filesWithoutRecordCount;
    if (count === 0) return;
    if (!window.confirm(`Delete ${count} file(s) from disk? These files have no database record. This cannot be undone.`)) return;
    setDeletingFilesWithoutRecord(true);
    setAuditError(null);
    try {
      await deleteFilesWithoutRecord();
      await runAudit();
    } catch (e: unknown) {
      setAuditError(e instanceof Error ? e.message : 'Failed to delete files');
    } finally {
      setDeletingFilesWithoutRecord(false);
    }
  };

  const config = useMemo(
    () => ({
      ...moduleConfig,
      getColumns,
      getToolbarIcon: () => <StorageIcon />,
      getTableActions,
      delete: {
        ...moduleConfig.delete,
        handler: async (id: number) => {
          await permanentDeleteFileAttachment(id);
          mutate();
        },
      },
    }),
    [mutate]
  );

  const deletableCount = (audit?.orphanedRecords ?? 0) + (audit?.noFilePath ?? 0);
  const filesWithoutRecordCount = audit?.filesWithoutRecord?.length ?? 0;
  const [activeTab, setActiveTab] = useState<'audit' | 'deleted'>('audit');
  const [auditSubTab, setAuditSubTab] = useState<'records' | 'files'>('records');

  return (
    <div className={cn('flex flex-col h-full min-h-0', colors.background)}>
      <div className="flex-1 min-h-0 overflow-auto">
        <div className="p-6 space-y-6">
          {/* Tabs */}
          <div className={cn('flex gap-1 border-b', colors.border)}>
            <button
              type="button"
              onClick={() => setActiveTab('audit')}
              className={cn(
                'px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 -mb-px transition-colors',
                activeTab === 'audit'
                  ? cn(colors.primaryFg, 'border-current')
                  : cn(colors.textMuted, 'border-transparent hover:opacity-80')
              )}
            >
              Audit
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('deleted')}
              className={cn(
                'px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 -mb-px transition-colors',
                activeTab === 'deleted'
                  ? cn(colors.primaryFg, 'border-current')
                  : cn(colors.textMuted, 'border-transparent hover:opacity-80')
              )}
            >
              Deleted files
            </button>
          </div>

          {activeTab === 'audit' && (
          <section className={cn('rounded-xl border overflow-hidden', colors.surface, colors.border)}>
            <div className={cn('px-5 py-4 border-b', colors.border)}>
              <h2 className={cn('text-base font-semibold', colors.text)}>Storage audit</h2>
              <p className={cn('text-sm mt-1', colors.textMuted)}>
                Compare FileAttachment records with files in <code className="text-xs bg-black/5 dark:bg-white/10 px-1 rounded">src/uploads</code>
              </p>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={runAudit}
                  disabled={auditLoading}
                  className={cn(
                    'px-4 py-2 text-sm font-medium rounded-lg',
                    colors.primary,
                    colors.primaryText,
                    'hover:opacity-90 disabled:opacity-50'
                  )}
                >
                  {auditLoading ? 'Checking…' : 'Run audit'}
                </button>
                {auditError && <span className="text-sm text-red-600 dark:text-red-400">{auditError}</span>}
              </div>

              {audit && (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                    {[
                      { label: 'Total records', value: audit.totalRecords, sub: 'In DB', color: '' },
                      { label: 'File on disk', value: audit.withFileOnDisk, sub: 'Match', color: 'text-green-600 dark:text-green-400' },
                      { label: 'Orphaned', value: audit.orphanedRecords, sub: 'Record, no file', color: 'text-amber-600 dark:text-amber-400' },
                      { label: 'No path', value: audit.noFilePath, sub: 'Empty path', color: '' },
                      { label: 'No record', value: filesWithoutRecordCount, sub: 'File, no DB', color: 'text-blue-600 dark:text-blue-400' },
                    ].map(({ label, value, sub, color }) => (
                      <div key={label} className={cn('p-4 rounded-lg border', colors.border)}>
                        <div className={cn('text-2xl font-semibold', color || colors.text)}>{value}</div>
                        <div className={cn('text-sm font-medium mt-0.5', colors.text)}>{label}</div>
                        <div className={cn('text-xs mt-0.5', colors.textMuted)}>{sub}</div>
                      </div>
                    ))}
                  </div>

                  {/* Sub-tabs: Records to delete | Files on disk with no record */}
                  <div className={cn('flex gap-1 border-b', colors.border)}>
                    <button
                      type="button"
                      onClick={() => setAuditSubTab('records')}
                      className={cn(
                        'px-3 py-2 text-sm font-medium rounded-t border-b-2 -mb-px transition-colors',
                        auditSubTab === 'records'
                          ? cn(colors.primaryFg, 'border-current')
                          : cn(colors.textMuted, 'border-transparent hover:opacity-80')
                      )}
                    >
                      Records to delete {audit.list.length > 0 && `(${audit.list.length})`}
                    </button>
                    <button
                      type="button"
                      onClick={() => setAuditSubTab('files')}
                      className={cn(
                        'px-3 py-2 text-sm font-medium rounded-t border-b-2 -mb-px transition-colors',
                        auditSubTab === 'files'
                          ? cn(colors.primaryFg, 'border-current')
                          : cn(colors.textMuted, 'border-transparent hover:opacity-80')
                      )}
                    >
                      Files on disk with no record {filesWithoutRecordCount > 0 && `(${filesWithoutRecordCount})`}
                    </button>
                  </div>

                  {auditSubTab === 'records' && (
                    <div className="space-y-3">
                      {audit.list.length > 0 && deletableCount > 0 && (
                        <button
                          type="button"
                          onClick={handleDeleteOrphaned}
                          disabled={deletingOrphaned}
                          className="px-4 py-2 text-sm font-medium rounded-lg border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400 disabled:opacity-50"
                        >
                          {deletingOrphaned ? 'Deleting…' : `Delete orphaned records (${deletableCount})`}
                        </button>
                      )}
                      <div className={cn('rounded-lg border overflow-hidden', colors.border)}>
                      {audit.list.length > 0 ? (
                        <div className="max-h-52 overflow-auto">
                          <table className="w-full text-sm">
                            <thead className={cn('sticky top-0 border-b', colors.border, colors.surface)}>
                              <tr>
                                <th className={cn('text-left py-2 px-4 font-medium', colors.text)}>ID</th>
                                <th className={cn('text-left py-2 px-4 font-medium', colors.text)}>File name</th>
                                <th className={cn('text-left py-2 px-4 font-medium', colors.text)}>Path</th>
                                <th className={cn('text-left py-2 px-4 font-medium', colors.text)}>Reason</th>
                              </tr>
                            </thead>
                            <tbody className={colors.text}>
                              {audit.list.map((item) => (
                                <tr key={item.id} className={cn('border-b last:border-b-0', colors.border)}>
                                  <td className="py-2 px-4 font-mono text-xs">{item.id}</td>
                                  <td className="py-2 px-4 truncate max-w-[180px]" title={item.fileName ?? ''}>{item.fileName ?? '—'}</td>
                                  <td className="py-2 px-4 truncate max-w-[200px] font-mono text-xs" title={item.filePath ?? ''}>{item.filePath ?? '—'}</td>
                                  <td className="py-2 px-4">{item.reason === 'orphaned' ? 'File missing' : 'No path'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className={cn('py-8 text-center text-sm', colors.textMuted)}>No records to delete.</div>
                      )}
                      </div>
                    </div>
                  )}

                  {auditSubTab === 'files' && (
                    <div className="space-y-3">
                      {filesWithoutRecordCount > 0 && (
                        <button
                          type="button"
                          onClick={handleDeleteFilesWithoutRecord}
                          disabled={deletingFilesWithoutRecord}
                          className="px-4 py-2 text-sm font-medium rounded-lg border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400 disabled:opacity-50"
                        >
                          {deletingFilesWithoutRecord ? 'Deleting…' : `Delete ${filesWithoutRecordCount} file(s) from disk`}
                        </button>
                      )}
                      <div className={cn('rounded-lg border overflow-hidden', colors.border)}>
                        {filesWithoutRecordCount > 0 ? (
                          <div className="max-h-52 overflow-auto">
                            <ul className={cn('divide-y', colors.border)}>
                              {audit.filesWithoutRecord.map((relPath) => (
                                <li key={relPath} className={cn('py-2 px-4 text-sm font-mono truncate', colors.text)} title={relPath}>
                                  {relPath}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ) : (
                          <div className={cn('py-8 text-center text-sm', colors.textMuted)}>No files on disk without a record.</div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </section>
          )}

          {activeTab === 'deleted' && (
          <section className={cn('rounded-xl border overflow-hidden', colors.surface, colors.border)}>
            <div className="min-h-[280px]">
              <GenericListPage<DeletedFileAttachment> useData={useData} config={config} />
            </div>
          </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default FilesStoragePage;
